import { API_ENDPOINTS } from '@/constants';
import type {
  GetRoutingProfilesOutputDTO,
  GetUsersRoutingProfilesOutputDTO,
  GetUsersRoutingProfilesParams,
  UpdateUserRoutingProfileInputDTO,
  GetMyRoutingProfileUsersOutputDTO,
  GetMyRoutingProfileQueuesOutputDTO,
  GetDisabledQueueQuickConnectsOutputDTO,
} from '@/types/routingProfile';
import type { ApiBuilder } from './types';

export const routingProfilesApi = (builder: ApiBuilder) => ({
  /**
   * Get all routing profiles (id + name)
   * GET /routing-profiles
   */
  getRoutingProfiles: builder.query<GetRoutingProfilesOutputDTO, void>({
    query: () => API_ENDPOINTS.ROUTING_PROFILES.BASE_URL,
    providesTags: ['RoutingProfiles'],
  }),

  /**
   * Get routing profile for users by email (direct reports only)
   * GET /routing-profiles/for-users?userEmails=...
   */
  getUsersRoutingProfiles: builder.query<
    GetUsersRoutingProfilesOutputDTO,
    GetUsersRoutingProfilesParams
  >({
    query: ({ userEmails }) => ({
      url: API_ENDPOINTS.ROUTING_PROFILES.FOR_USERS,
      params: { userEmails },
    }),
    providesTags: ['RoutingProfiles'],
  }),

  /**
   * Assign user to a routing profile
   * POST /routing-profiles/:routingProfileId
   */
  updateUserRoutingProfile: builder.mutation<
    void,
    { routingProfileId: string; body: UpdateUserRoutingProfileInputDTO }
  >({
    query: ({ routingProfileId, body }) => ({
      url: `${API_ENDPOINTS.ROUTING_PROFILES.BASE_URL}/${routingProfileId}`,
      method: 'POST',
      body,
    }),
    invalidatesTags: ['RoutingProfiles'],
  }),

  getMyRoutingProfileUsers: builder.query<
    GetMyRoutingProfileUsersOutputDTO,
    void
  >({
    query: () => API_ENDPOINTS.ROUTING_PROFILES.MY_TEAM,
    providesTags: ['RoutingProfiles'],
  }),

  /**
   * Get queues associated with the current user's routing profile
   * GET /routing-profiles/my-queues
   */
  getMyRoutingProfileQueues: builder.query<
    GetMyRoutingProfileQueuesOutputDTO,
    void
  >({
    query: () => API_ENDPOINTS.ROUTING_PROFILES.MY_QUEUES,
    providesTags: ['RoutingProfiles'],
  }),

  /**
   * Quick connect IDs pointing at disabled queues (hidden from transfer lists)
   * GET /routing-profiles/disabled-queue-quick-connects
   */
  getDisabledQueueQuickConnects: builder.query<
    GetDisabledQueueQuickConnectsOutputDTO,
    void
  >({
    query: () => API_ENDPOINTS.ROUTING_PROFILES.DISABLED_QUEUE_QUICK_CONNECTS,
    providesTags: ['RoutingProfiles'],
    // Backend serves this from a 5-minute cache; no need to refetch more often.
    keepUnusedDataFor: 300,
  }),
});
